import React, { Fragment, useEffect, useContext } from "react";
import { allPlays } from "games/allPlays";
import _ from "lodash";
import { makeStyles } from "@material-ui/core/styles";
import {
  getOppositeSide,
  getTeamSideFromId,
  getPlayTypeInfo,
  fieldExistsOnPlayType
} from "games/utils";
import Button from "@material-ui/core/Button";
import PlayTextInput from "./inputs/PlayTextInput";
import PlayNumberInput from "./inputs/PlayNumberInput";
import PlayCheckboxInput from "./inputs/PlayCheckboxInput";
import PlayRadioGroupInput from "./inputs/PlayRadioGroupInput";
import PlayAutocompleteInput from "./inputs/PlayAutocompleteInput";
import PlayTimeInput from "./inputs/PlayTimeInput";
import FormLabel from "@material-ui/core/FormLabel";
import { PlayByPlayContext } from "games/model/PlayByPlayContext";

const useStyles = makeStyles({
  input: {
    marginRight: "10px",
    marginBottom: "10px"
  },
  wFull: {
    width: "100%"
  },
  fieldsInnerContainer: {
    width: "100%",
    display: "flex",
    flexWrap: "wrap"
  }
});

const makeTeamOption = (seasonTeam) => {
  const { id, team } = seasonTeam;
  const schoolName = _.get(team, "school.name");
  const teamName = _.get(team, "name");
  return { id: id, name: `${teamName} (${schoolName})` };
};

const getTeamOptions = (game) => {
  const homeSeasonTeam = _.get(game, "home.season", {});
  const awaySeasonTeam = _.get(game, "away.season", {});
  return [makeTeamOption(homeSeasonTeam), makeTeamOption(awaySeasonTeam)];
};

const safetyTypeOptions = [
  {
    id: "safetyBallCarrierSteppedOut",
    name: "Ball Carrier Stepped out of End Zone"
  },
  { id: "safetyBallCarrierTackled", name: "Ball Carrier Tackled in End Zone" },
  { id: "safetyBallSnapped", name: "Ball Snapped out of End Zone" },
  { id: "safetyFumbled", name: "Fumbled out of End Zone" },
  { id: "safetyPenalty", name: "Penalty in End Zone" },
  { id: "safetyPuntBlocked", name: "Punt Blocked out of End Zone" }
];

const fieldGoalReturnOptions = [
  { id: "missed", name: "Missed" },
  { id: "blocked", name: "Blocked" }
];

const PlaySuggestedPlayTypes = ({ options, handleButtonClick, classes }) => {
  return (
    <div className={classes.wFull}>
      <h4 className={classes.wFull}>Suggested Play Types:</h4>
      {options.map((option) => {
        return (
          <Button
            key={option.id}
            onClick={() => handleButtonClick(option.id, "playType")}
            variant="outlined"
            color="primary"
            className={classes.input}
          >
            {option.name}
          </Button>
        );
      })}
    </div>
  );
};

const PlayBaseFields = (props) => {
  const {
    usePlayByPlay: [state]
  } = useContext(PlayByPlayContext);
  const { game } = state;

  const classes = useStyles();
  const teamOptions = getTeamOptions(game);
  let playTypeRef = null;
  const setPlayTypeRef = (el) => {
    playTypeRef = el;
  };
  const inputProps = {
    classes,
    handleInputChange: props.handleInputChange,
    handleButtonClick: props.handleButtonClick,
    handleDropdownChange: props.handleDropdownChange,
    play: props.play
  };

  const getPlayerFieldLabel = (playerType) => {
    const { play } = props;
    const map = {
      pass: ["Passer", "Receiver"],
      PATPass: ["Passer", "Receiver"],
      pitch: ["Passer", "Receiver"],
      interception: ["Passer", "Interceptor"],
      muffedPunt: ["Kicker", "Receiver"]
    };

    const players = _.get(map, play.playType, ["Player"]);
    return playerType === "player1" ? players[0] : players[1];
  };

  const getPlayerOptions = (inputName) => {
    const { play } = props;
    const playInfo = getPlayTypeInfo(play.playType);
    const playerSide = _.get(playInfo, ["players", inputName]);
    const teamInPossessionId = _.get(play, "teamInPossessionId");
    const teamInPossessionSide = getTeamSideFromId(game, teamInPossessionId);
    const teamSide =
      playerSide === "defense"
        ? getOppositeSide(teamInPossessionSide)
        : teamInPossessionSide;

    const players = _.get(game, [teamSide, "season", "players"], []).map(
      (player) => {
        const playerName = _.get(player, "person.name");
        const playerNumber = _.get(player, "jerseyNumber");
        const playerId = _.get(player, "id");
        const playerDisplay = playerNumber
          ? `${playerNumber} - ${playerName}`
          : playerName;

        return {
          id: playerId,
          name: playerDisplay
        };
      }
    );
    return players;
  };

  useEffect(() => {
    if (!playTypeRef) return;
    playTypeRef.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Fragment>
      {!props.play.playType && props.playTypeSuggestions && (
        <PlaySuggestedPlayTypes
          options={props.playTypeSuggestions}
          {...inputProps}
        />
      )}

      <PlayAutocompleteInput
        label="Play Type"
        inputName="playType"
        options={allPlays.filter((p) => p.id !== "endOfQuarter")}
        inputRef={setPlayTypeRef}
        {...inputProps}
      />

      <PlayAutocompleteInput
        label="Safety Type"
        inputName="safetyType"
        options={safetyTypeOptions}
        {...inputProps}
      />

      <PlayAutocompleteInput
        label="Field Goal Return Type"
        inputName="fieldGoalReturnType"
        options={fieldGoalReturnOptions}
        {...inputProps}
      />

      <PlayAutocompleteInput
        label="Team In Possession"
        inputName="teamInPossessionId"
        options={teamOptions}
        {...inputProps}
      />

      <PlayAutocompleteInput
        label="Recovered by"
        inputName="muffedPuntRecoveredByTeamId"
        options={teamOptions}
        {...inputProps}
      />

      <PlayCheckboxInput
        label="Scoring"
        inputName="includesScore"
        {...inputProps}
      />
      <PlayCheckboxInput
        label="Blocked"
        inputName="PATBlocked"
        {...inputProps}
      />

      <PlayAutocompleteInput
        label="Team that Called Timeout"
        inputName="teamCalledTimeout"
        options={teamOptions}
        {...inputProps}
      />

      <PlayNumberInput label="Down" inputName="down" {...inputProps} />

      {fieldExistsOnPlayType(props.play.playType, "distanceToGo") && (
        <div className={classes.wFull}>
          <FormLabel component="legend">Distance To Go</FormLabel>
          <div className={classes.fieldsInnerContainer}>
            {!props.play.isGoalDistanceToGo && (
              <PlayNumberInput
                label="Number"
                inputName="distanceToGo"
                {...inputProps}
              />
            )}
            <PlayCheckboxInput
              label="DTG Goal"
              inputName="isGoalDistanceToGo"
              overrideFieldExists="distanceToGo"
              {...inputProps}
            />
          </div>
        </div>
      )}

      <PlayAutocompleteInput
        label="Yard Line Team"
        inputName="yardLineTeamId"
        options={teamOptions}
        {...inputProps}
      />

      <PlayNumberInput label="Yard Line" inputName="yardLine" {...inputProps} />

      <PlayAutocompleteInput
        autoFocus={true}
        label={getPlayerFieldLabel("player1")}
        inputName="player1Id"
        options={getPlayerOptions("player1")}
        {...inputProps}
      />

      <PlayAutocompleteInput
        label="Ball Spot Team"
        inputName="ballSpotTeamId"
        options={teamOptions}
        {...inputProps}
      />

      <PlayNumberInput label="Ball Spot" inputName="ballSpot" {...inputProps} />

      <PlayAutocompleteInput
        label={getPlayerFieldLabel("player2")}
        inputName="player2Id"
        options={getPlayerOptions("player2")}
        {...inputProps}
      />

      <PlayCheckboxInput
        label="Touchdown"
        inputName="includesTouchdown"
        {...inputProps}
      />

      {props.play.quarter === 2 && (
        <PlayTimeInput
          label="Time"
          helperText="When Halftime Ends"
          inputName="timeEndOfHalftime"
          {...inputProps}
        />
      )}

      <PlayCheckboxInput
        label="Fumble"
        inputName="includesFumble"
        overrideFieldExists="fumble"
        {...inputProps}
      />

      {props.play.includesFumble && props.play.playType !== "fieldGoal" && (
        <PlayRadioGroupInput
          inputName="fumble"
          options={[
            { id: "held", name: "Held" },
            { id: "lost", name: "Lost" }
          ]}
          row
          {...inputProps}
        />
      )}

      <PlayCheckboxInput
        label="Converts First Down"
        inputName="convertsFirstDown"
        {...inputProps}
      />

      <PlayTextInput
        label="Time Remaining in Quarter"
        inputName="timeRemaining"
        {...inputProps}
      />

      {props.play.playType &&
        (fieldExistsOnPlayType(props.play.playType, "quarter") ||
          fieldExistsOnPlayType(props.play.playType, "lengthOfPlay")) && (
          <Fragment>
            <h5 className={classes.wFull}>
              The remaining field(s) are calculated automatically, but are
              available here in case they need to be overwritten:
            </h5>
          </Fragment>
        )}

      <PlayNumberInput
        tabIndex={-1}
        label="Quarter"
        inputName="quarter"
        {...inputProps}
      />

      <PlayNumberInput
        tabIndex={-1}
        label="Length of Play"
        inputName="lengthOfPlay"
        {...inputProps}
      />

      <PlayTextInput
        multiline
        tabIndex={-1}
        label="Notes"
        inputName="notes"
        {...inputProps}
      />
    </Fragment>
  );
};

export default PlayBaseFields;
